/**
 * "A draft is waiting": the gated asks whose band sits on the same page (PDP: Owners section and
 * footer, `OwnerHeroAsk` in the rail and under the reader; guide: the seated ask and the rail ask)
 * say "Finish your owner review" when this tab holds a started band draft for the product — after
 * a reload or the email-link detour. One key per product, whichever page's band wrote it (PDP,
 * category strip, guide, review post): a draft started on one surface finishes on another. The band's own restore does the rest
 * (`OwnerNoteBand` reads the same key on open and lands on the saved step).
 *
 * Client-only, after mount: the server HTML (and the first client render) always carry the plain
 * ask, so hydration matches and a cached page never says it (Constraint 6ead9e26). Tests
 * `owner-draft-waiting.spec.ts`.
 */
import { useEffect, useState } from 'react'

/** The band's per-tab draft key for a product (`OwnerNoteBand`'s `draftKey`), on every gated
 *  band host. The `product:` segment is the key's original name (PDP, slice B), kept so a draft
 *  written before slice D still restores. */
export function bandDraftKey(nodeId: string): string {
  return `sg:owner-band-draft:product:${nodeId}`
}

/**
 * Is this stored band draft one the reader started? The band writes its draft on open, so an
 * untouched step 1 (no answer, no sentence) is not one. Anything unreadable is not one either.
 */
export function bandDraftStarted(raw: string | null): boolean {
  if (!raw) {
    return false
  }
  try {
    const d = JSON.parse(raw) as {
      step?: unknown
      note?: unknown
      answers?: Record<string, unknown> | null
    } | null
    if (!d || typeof d !== 'object' || !d.answers || typeof d.answers !== 'object') {
      return false
    }
    if (d.step === 2 || d.step === 3) {
      return true
    }
    const a = d.answers
    return (
      (typeof d.note === 'string' && d.note.trim() !== '') ||
      Boolean(a.ownership_length) ||
      Boolean(a.still_using) ||
      (Array.isArray(a.use_cases) && a.use_cases.length > 0) ||
      typeof a.buy_again === 'boolean'
    )
  } catch {
    return false
  }
}

/**
 * After mount, does this tab hold a started band draft for `nodeId`? Always false on the server
 * and on the first render; false when `enabled` is (an ungated or handed-off ask). `recheck`
 * re-reads when it changes (the section's band closing ends or keeps the draft).
 */
export function useBandDraftWaiting(nodeId: string, enabled: boolean, recheck?: unknown): boolean {
  const [waiting, setWaiting] = useState(false)
  useEffect(() => {
    if (!enabled) {
      setWaiting(false)
      return
    }
    let raw: string | null = null
    try {
      raw = window.sessionStorage.getItem(bandDraftKey(nodeId))
    } catch {
      // Blocked storage: no draft to finish.
    }
    setWaiting(bandDraftStarted(raw))
  }, [nodeId, enabled, recheck])
  return waiting
}
